import { Observable, Subject, Subscriber } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import {
  WebSocketConnectionState,
  WebSocketRequestOptions,
  WebSocketServiceConfig,
  ICommandRequest,
  ICommandResponse,
} from './../model/web-socket';

export abstract class AbstractWebSocketService {
  private readonly CONNECTION_TIMEOUT = 5000;

  protected config: WebSocketServiceConfig;
  private connectionStateSubject: Subject<WebSocketConnectionState>;

  private socket: WebSocketSubject<any> | null = null;
  private connectionState: WebSocketConnectionState = WebSocketConnectionState.Closed;

  public get state$(): Observable<WebSocketConnectionState> {
    return this.connectionStateSubject;
  }

  public get state(): WebSocketConnectionState {
    return this.connectionState;
  }

  public constructor(config: WebSocketServiceConfig) {
    this.config = config;
    this.connectionStateSubject = new Subject<WebSocketConnectionState>();
  }

  connect(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.socket === null) {
        this.subscribeAndWait(observer, WebSocketConnectionState.Opened);
        this.connectInternal();
      } else {
        observer.error('Connection is not closed');
      }
    });
  }

  disconnect(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.socket === null) {
        observer.complete();
      } else {
        this.subscribeAndWait(observer, WebSocketConnectionState.Closed);
        this.disconnectInternal();
      }
    });
  }

  ensureConnected(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.socket === null) {
        let subscruption = this.connect().subscribe(
          (result) => {
            subscruption.unsubscribe();
            observer.next();
            observer.complete();
          },
          (error) => {
            subscruption.unsubscribe();
            observer.error(error);
          },
        );
        return;
      }

      if (this.connectionState === WebSocketConnectionState.Connecting) {
        this.subscribeAndWait(observer, WebSocketConnectionState.Opened);
        return;
      }

      observer.next();
      observer.complete();
    });
  }

  protected sendRequest(request: ICommandRequest, options: WebSocketRequestOptions): Observable<ICommandResponse | Blob> {
    return new Observable<any>((observer) => {
      if (this.connectionState !== WebSocketConnectionState.Opened) {
        observer.error('Connection is not opened');
        return;
      }

      let receivedBlobCounter_ = 0;
      let receivedJsonCounter_ = 0;
      let isCommandResultReceived_ = false;
      let subscription = this.socket!.subscribe({
        next: (response: ICommandResponse | Blob) => {
          if (response instanceof Blob) {
            if (isCommandResultReceived_ && receivedBlobCounter_ < options.expectedBlobs) {
              ++receivedBlobCounter_;
              observer.next(response);
            }
          } else {
            if (response.command === request.command) {
              if (isCommandResultReceived_) {
                subscription.unsubscribe();
                observer.error('Command result already received.');
                return;
              }

              if (response.progress !== undefined) {
                observer.next(response);
                return;
              }

              if (response.result === false) {
                subscription.unsubscribe();
                observer.error(response.error);
                return;
              }

              ++receivedJsonCounter_;
              if (receivedJsonCounter_ >= options.expectedJsons) {
                isCommandResultReceived_ = true;
              }
              observer.next(response);
            }
          }

          if (isCommandResultReceived_ && receivedBlobCounter_ >= options.expectedBlobs) {
            subscription.unsubscribe();
            observer.complete();
          }
        },
        error: (error) => {
          subscription.unsubscribe();
          observer.error(error);
        },
      });
      this.socket!.next(request);
    });
  }

  private subscribeAndWait(observer: Subscriber<void>, exectedState: WebSocketConnectionState) {
    let subscription = this.connectionStateSubject.pipe(timeout(this.CONNECTION_TIMEOUT)).subscribe({
      next: (state) => {
        if (state === exectedState) {
          subscription.unsubscribe();
          observer.next();
          observer.complete();
        }
      },
      error: (error) => {
        subscription.unsubscribe();
        observer.error(error);
      },
    });
  }

  private connectInternal(): void {
    this.setConnectionState(WebSocketConnectionState.Connecting);
    this.socket = this.createWebSocketSubject();
    this.socket.subscribe((r) => {});
  }

  private disconnectInternal(): void {
    this.setConnectionState(WebSocketConnectionState.Disconnecting);
    if (this.socket) {
      this.socket.complete();
    }
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionStateSubject.next(state);
    }
  }

  private createWebSocketSubject(): WebSocketSubject<any> {
    let config = this.createWebSocketConfig();
    return new WebSocketSubject<any>(config);
  }

  private createWebSocketConfig(): WebSocketSubjectConfig<any> {
    return {
      url: this.config.url,
      protocol: this.config.protocol,
      openObserver: {
        next: (param) => {
          this.setConnectionState(WebSocketConnectionState.Opened);
        },
        error: (error) => {
          this.connectionStateSubject.error(error);
          this.setConnectionState(WebSocketConnectionState.Closed);
        },
      },
      closeObserver: {
        next: (param) => {
          this.socket = null;
          this.setConnectionState(WebSocketConnectionState.Closed);
        },
        error: (error) => {
          this.socket = null;
          this.connectionStateSubject.error(error);
        },
      },
      serializer: (message) => {
        return this.serialize(message);
      },
      deserializer: (event) => {
        return this.deserialize(event);
      },
    };
  }

  private serialize(message: any): any {
    if (message instanceof Blob) {
      return message;
    } else {
      return JSON.stringify(message);
    }
  }

  private deserialize(event: MessageEvent) {
    if (event.data instanceof Blob) {
      return event.data;
    } else {
      return JSON.parse(event.data);
    }
  }
}
